import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select, { components } from "react-select";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CfLoader from "../../common/CfLoader/CfLoader";
import toast from "react-hot-toast";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import { useTranslation } from "react-i18next";
import {
  SelectSpinner,
  SelectLoadingIndicator,
  SelectLoadingMessage,
  SelectLoadingStatus,
} from "../../common/SelectLoader/SelectLoader";

const PROGRAMME_SLOW_LOAD_THRESHOLD_MS = 3000;

const isCoarsePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

const WithoutQrIssuerSelectOption = (props) => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      "data-issuer-code": props.data.code,
    }}
  />
);

const LegacyTemplateDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeSection, setActiveSection] = useState("legacy");
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Certificate selection state
  const [issuerList, setIssuerList] = useState([]);
  const [programmeList, setProgrammeList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [templateList, setTemplateList] = useState([]);

  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [postIssuerName, setPostIssuerName] = useState("");
  const [postIssuerCode, setPostIssuerCode] = useState("");
  const [postProgrammeName, setPostProgrammeName] = useState("");
  const [postProgrammeDisplayName, setPostProgrammeDisplayName] = useState("");
  const [postProgrammeCode, setPostProgrammeCode] = useState("");
  const [postBatchName, setPostBatchName] = useState("");
  const [postBatchCode, setPostBatchCode] = useState("");
  const [postCredentialTemplate, setPostCredentialTemplate] = useState("");
  const [postCertificateName, setPostCertificateName] = useState("");

  const [showCustomProgrammeInput, setShowCustomProgrammeInput] =
    useState(false);
  const [showCustomBatchInput, setShowCustomBatchInput] = useState(false);
  const [customProgrammeName, setCustomProgrammeName] = useState("");
  const [customBatchName, setCustomBatchName] = useState("");
  const [isOthersSelected, setIsOthersSelected] = useState(false);
  const [isRegularProgramme, setIsRegularProgramme] = useState(false);
  const [isLoadingIssuers, setIsLoadingIssuers] = useState(false);
  const [programmeStatus, setProgrammeStatus] = useState("idle");
  const [isProgrammeLoadSlow, setIsProgrammeLoadSlow] = useState(false);
  const programmeRequestRef = useRef({ id: 0, orgCode: null, inFlight: false });
  const isLoadingProgrammes = programmeStatus === "loading";
  const isProgrammeSelectDisabled =
    programmeStatus === "idle" ||
    programmeStatus === "error" ||
    (programmeStatus === "loaded" && programmeList.length === 0);
  const programmeSelectRef = useRef(null);
  const pendingProgrammeMenuOpenRef = useRef(false);
  const [programmeMenuOpenRequest, setProgrammeMenuOpenRequest] = useState(0);
  const [isProgrammeMenuOpen, setIsProgrammeMenuOpen] = useState(false);
  const programmeSlowLoadingHint =
    isLoadingProgrammes && isProgrammeLoadSlow
      ? t("verify:programmes_slow_loading_hint", {
          issuer: selectedIssuer?.label || "",
        })
      : null;

  const steps = [
    t("verify:select_certificate"),
    t("verify:details"),
    t("verify:payment"),
    t("verify:summary"),
  ];

  // Load data from location state on component mount
  useEffect(() => {
    const stateData = location.state;
    if (stateData) {
      if (stateData.postIssuerName) setPostIssuerName(stateData.postIssuerName);
      if (stateData.postIssuerCode) setPostIssuerCode(stateData.postIssuerCode);
      if (stateData.postProgrammeName)
        setPostProgrammeName(stateData.postProgrammeName);
      if (stateData.postProgrammeDisplayName)
        setPostProgrammeDisplayName(stateData.postProgrammeDisplayName);
      if (stateData.postProgrammeCode)
        setPostProgrammeCode(stateData.postProgrammeCode);

      //  we have commented the batch code for now, can be used in future.

      // if (stateData.postBatchName) setPostBatchName(stateData.postBatchName);
      // if (stateData.postBatchCode) setPostBatchCode(stateData.postBatchCode);
      if (stateData.postCredentialTemplate)
        setPostCredentialTemplate(stateData.postCredentialTemplate);
      if (stateData.postCertificateName)
        setPostCertificateName(stateData.postCertificateName);
      const isOthersSelectedFlag =
        typeof stateData.isOthersSelected === "boolean"
          ? stateData.isOthersSelected
          : stateData.isOthersSelected === "true";
      setIsOthersSelected(isOthersSelectedFlag);
      if (typeof stateData.isRegularProgramme === "boolean") {
        setIsRegularProgramme(stateData.isRegularProgramme);
      } else if (
        stateData.postProgrammeCode &&
        stateData.postProgrammeCode !== "Others"
      ) {
        setIsRegularProgramme(true);
      }
      if (stateData.customProgrammeName)
        setCustomProgrammeName(stateData.customProgrammeName);

      //  we have commented the batch code for now, can be used in future.

      // if (stateData.customBatchName)
      //   setCustomBatchName(stateData.customBatchName);

      if (stateData.postIssuerName && stateData.postIssuerCode) {
        setSelectedIssuer({
          value: stateData.postIssuerName,
          label: stateData.postIssuerName,
          code: stateData.postIssuerCode,
        });
      }

      if (isOthersSelectedFlag) {
        setSelectedProgramme({
          value: "Others",
          label: `${t("verify:others")}`,
          code: "Others",
        });
        setSelectedBatch({
          value: "Others",
          label: `${t("verify:others")}`,
          code: "Others",
        });
        setShowCustomProgrammeInput(true);

        //  we have commented the batch code for now, can be used in future.

        // setShowCustomBatchInput(true);
      } else {
        if (stateData.postProgrammeName && stateData.postProgrammeCode) {
          const programmeDisplayLabel = stateData.postProgrammeDisplayName
            ? `${stateData.postProgrammeDisplayName} (${stateData.postProgrammeName})`
            : stateData.postProgrammeName;
          setSelectedProgramme({
            value: stateData.postProgrammeName,
            label: programmeDisplayLabel,
            code: stateData.postProgrammeCode,
          });
        }

        //  we have commented the batch code for now, can be used in future.

        // if (stateData.postBatchName && stateData.postBatchCode) {
        //   setSelectedBatch({
        //     value: stateData.postBatchName,
        //     label: stateData.postBatchName,
        //     code: stateData.postBatchCode,
        //   });
        // }

        if (stateData.postCredentialTemplate || stateData.postCertificateName) {
          setSelectedTemplate({
            value: stateData.postCredentialTemplate,
            label: stateData.postCertificateName || "",
          });
        }
      }
    }
  }, [location.state, t]);

  useEffect(() => {
    if (issuerList.length > 0 && postIssuerCode) {
      const issuerOption = issuerList.find(
        (issuer) => issuer.org_code === postIssuerCode,
      );
      if (issuerOption) {
        setSelectedIssuer({
          value: issuerOption.org_name,
          label: issuerOption.org_name,
          code: issuerOption.org_code,
        });
        getProgrammeDetails(postIssuerCode);
      }
    }
  }, [issuerList, postIssuerCode]);

  useEffect(() => {
    if (programmeList.length > 0 && postProgrammeCode) {
      if (isOthersSelected) {
        setSelectedProgramme({
          value: "Others",
          label: `${t("verify:others")}`,
          code: "Others",
        });
        setShowCustomProgrammeInput(true);

        //  we have commented the batch code for now, can be used in future.

        // if (postBatchCode === "Others") {
        //   setSelectedBatch({
        //     value: "Others",
        //     label: `${t("verify:others")}`,
        //     code: "Others",
        //   });
        //   setShowCustomBatchInput(true);
        // }
      } else {
        const programmeOption = programmeList.find(
          (programme) => programme.programme_code === postProgrammeCode,
        );
        if (programmeOption) {
          const displayLabel = programmeOption.programme_display_name
            ? `${programmeOption.programme_display_name} (${programmeOption.programme_name})`
            : programmeOption.programme_name;
          setIsRegularProgramme(true);
          setSelectedProgramme({
            value: programmeOption.programme_name,
            label: displayLabel,
            code: programmeOption.programme_code,
          });
          getBatchDetails(postIssuerCode, postProgrammeCode);
        }
      }
    }
  }, [
    programmeList,
    postProgrammeCode,
    isOthersSelected,
    // postBatchCode,
    postIssuerCode,
  ]);

  //  we have commented the batch code for now, can be used in future.

  // useEffect(() => {
  //   if (batchList.length > 0 && postBatchCode && !isOthersSelected) {
  //     const batchOption = batchList.find(
  //       (batch) =>
  //         batch.batch_code === postBatchCode ||
  //         batch.batch_name === postBatchName,
  //     );

  //     if (batchOption) {
  //       setSelectedBatch({
  //         value: batchOption.batch_name,
  //         label: batchOption.batch_name,
  //         code: batchOption.batch_code,
  //       });

  //       setPostBatchCode(batchOption.batch_code);
  //       getTemplateDetails(postIssuerCode, batchOption.batch_code);
  //     }
  //   }
  // }, [
  //   batchList,
  //   postBatchCode,
  //   postBatchName,
  //   isOthersSelected,
  //   postIssuerCode,
  // ]);

  useEffect(() => {
    if (
      templateList.length > 0 &&
      (postCredentialTemplate || postCertificateName) &&
      !isOthersSelected
    ) {
      let templateOption = null;

      if (postCredentialTemplate) {
        templateOption = templateList.find(
          (t) => t.issuer_credential_template_id === postCredentialTemplate,
        );
      }
      if (!templateOption && postCertificateName) {
        templateOption = templateList.find(
          (t) => t.certificate_name === postCertificateName,
        );
        if (templateOption) {
          setPostCredentialTemplate(
            templateOption.issuer_credential_template_id,
          );
        }
      }
      if (templateOption) {
        setSelectedTemplate({
          value: templateOption.issuer_credential_template_id,
          label: templateOption.certificate_name,
        });
      }
    }
  }, [
    templateList,
    postCredentialTemplate,
    postCertificateName,
    isOthersSelected,
  ]);

  useEffect(() => {
    const stateData = location.state;
    if (stateData && stateData.isOthersSelected) {
      setShowCustomProgrammeInput(true);
      setShowCustomBatchInput(true);

      if (stateData.customProgrammeName) {
        setCustomProgrammeName(stateData.customProgrammeName);
        setPostProgrammeName(stateData.customProgrammeName);
      }

      //  we have commented the batch code for now, can be used in future.

      // if (stateData.customBatchName) {
      //   setCustomBatchName(stateData.customBatchName);
      //   setPostBatchName(stateData.customBatchName);
      // }
    }
  }, [location.state]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "vc") {
      navigate("/verifier/validate");
    } else if (section === "legacy") {
      navigate("/verifier/validate", {
        state: { showLegacySection: "legacy" },
      });
    }
  };

  const getIssuerDetails = async () => {
    setIsLoadingIssuers(true);
    try {
      const response = await GET(EntityConstants.GET_ISSUER);
      if (response.ok) {
        const data = await response.json();
        setIssuerList(data.data);
      }
    } catch (error) {
      console.error("Error in fetching issuer programme details:", error);
    } finally {
      setIsLoadingIssuers(false);
    }
  };

  const getProgrammeDetails = async (orgCode) => {
    const current = programmeRequestRef.current;
    if (current.inFlight && current.orgCode === orgCode) return;

    const requestId = current.id + 1;
    programmeRequestRef.current = { id: requestId, orgCode, inFlight: true };
    const isLatestRequest = () => programmeRequestRef.current.id === requestId;

    setProgrammeStatus("loading");
    try {
      const response = await GET(
        `${EntityConstants.GET_PROGRAMME}?code=${orgCode}`,
      );
      if (!isLatestRequest()) return;
      if (response.ok) {
        const data = await response.json();
        if (!isLatestRequest()) return;
        setProgrammeList(Array.isArray(data.data) ? data.data : []);
        setProgrammeStatus("loaded");
      } else {
        setProgrammeStatus("error");
      }
    } catch (error) {
      console.error("Error in fetching programme details:", error);
      if (isLatestRequest()) setProgrammeStatus("error");
    } finally {
      if (isLatestRequest()) programmeRequestRef.current.inFlight = false;
    }
  };

  useEffect(() => {
    setIsProgrammeLoadSlow(false);
    if (!isLoadingProgrammes) return undefined;
    const timer = setTimeout(
      () => setIsProgrammeLoadSlow(true),
      PROGRAMME_SLOW_LOAD_THRESHOLD_MS,
    );
    return () => clearTimeout(timer);
  }, [isLoadingProgrammes, postIssuerCode]);

  const getBatchDetails = async (orgCode, programmeCode) => {
    try {
      const response = await GET(
        `${EntityConstants.GET_BATCHES}?programme_code=${programmeCode}&issuer_org_code=${orgCode}&`,
      );
      if (response.ok) {
        const data = await response.json();
        setBatchList(data.data);
      }
    } catch (error) {
      console.error("Error in fetching batch details:", error);
    }
  };

  const getTemplateDetails = async (orgCode, batchCode) => {
    try {
      const response = await GET(
        `${EntityConstants.GET_TEMPLATES}?org_code=${orgCode}&programme_code=${postProgrammeCode}&batch_code=${batchCode}`,
      );

      if (response.ok) {
        const data = await response.json();
        setTemplateList(data.data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error(t("verify:failed_fetch_templates"));
    }
  };

  const handleIssuerChange = (selectedOption) => {
    setSelectedIssuer(selectedOption);
    setPostIssuerName(selectedOption.value);
    setPostIssuerCode(selectedOption.code);

    setSelectedProgramme(null);
    setSelectedBatch(null);
    setSelectedTemplate(null);
    setPostProgrammeName("");
    setPostProgrammeDisplayName("");
    setPostProgrammeCode("");
    setPostBatchName("");
    setPostBatchCode("");
    setPostCredentialTemplate("");
    setPostCertificateName("");

    setProgrammeList([]);
    setBatchList([]);
    setTemplateList([]);
    setIsRegularProgramme(false);

    getProgrammeDetails(selectedOption.code);
    if (!isCoarsePointer()) {
      pendingProgrammeMenuOpenRef.current = true;
      setProgrammeMenuOpenRequest((count) => count + 1);
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      issuer: undefined,
    }));
  };

  useEffect(() => {
    if (!pendingProgrammeMenuOpenRef.current || programmeStatus === "idle") {
      return;
    }
    pendingProgrammeMenuOpenRef.current = false;
    const programmeSelect = programmeSelectRef.current;
    if (isProgrammeSelectDisabled || !programmeSelect) return;
    programmeSelect.focus();
    programmeSelect.openMenu("first");
  }, [programmeStatus, programmeMenuOpenRequest, isProgrammeSelectDisabled]);

  const handleProgrammeChange = (selectedOption) => {
    setSelectedProgramme(selectedOption);

    if (selectedOption.value === "Others") {
      setIsOthersSelected(true);
      setIsRegularProgramme(false);
      setShowCustomProgrammeInput(true);
      setPostProgrammeName("");
      setPostProgrammeDisplayName("");
      setPostProgrammeCode("Others");

      //  we have commented the batch code for now, can be used in future.

      // const othersBatch = {
      //   value: "Others",
      //   label: `${t("verify:others")}`,
      //   code: "Others",
      // };
      // setSelectedBatch(othersBatch);
      // setPostBatchName("Others");
      // setPostBatchCode("Others");
      // setShowCustomBatchInput(true);

      setTemplateList([]);
      setSelectedTemplate(null);
      setPostCredentialTemplate("");
      setPostCertificateName("");
    } else {
      setIsOthersSelected(false);
      setIsRegularProgramme(true);
      setShowCustomProgrammeInput(false);
      setShowCustomBatchInput(false);
      setCustomProgrammeName("");
      setCustomBatchName("");
      setPostProgrammeName(selectedOption.value);
      setPostProgrammeDisplayName(selectedOption.displayLabel);
      setPostProgrammeCode(selectedOption.code);

      setSelectedBatch(null);
      setSelectedTemplate(null);
      setPostBatchName("");
      setPostBatchCode("");
      setPostCredentialTemplate("");
      setPostCertificateName("");

      setBatchList([]);
      setTemplateList([]);
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      programmeName: undefined,
    }));
  };

  const handleCustomProgrammeChange = (e) => {
    const value = e.target.value;
    setCustomProgrammeName(value);
    setPostProgrammeName(value);

    if (value.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        programmeName: undefined,
      }));
    }
  };

  const handleCustomBatchChange = (e) => {
    const value = e.target.value;
    setCustomBatchName(value);
    setPostBatchName(value);

    if (value.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        batchName: undefined,
      }));
    }
  };

  const handleBatchChange = (selectedOption) => {
    setSelectedBatch(selectedOption);
    setPostBatchName(selectedOption.value);
    setPostBatchCode(selectedOption.code);

    setSelectedTemplate(null);
    setPostCredentialTemplate("");
    setPostCertificateName("");

    setTemplateList([]);

    if (!isOthersSelected && selectedOption.value !== "Others") {
      getTemplateDetails(postIssuerCode, selectedOption.code);
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      batchName: undefined,
    }));
  };

  //  we have commented the batch code for now, can be used in future.

  // const handleTemplateChange = (selectedOption) => {
  //   setSelectedTemplate(selectedOption);
  //   setPostCredentialTemplate(selectedOption.value);
  //   setPostCertificateName(selectedOption.label);
  //   setErrors((prevErrors) => ({
  //     ...prevErrors,
  //     credentialTemplate: undefined,
  //   }));
  // };

  const fetchFormData = async () => {
    let queryParams = new URLSearchParams({
      org_code: postIssuerCode,
      category_code: postCredentialTemplate,
    }).toString();
    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFY_FORM_DATA}?${queryParams}`,
      );

      if (response.ok) {
        const data = await response.json();
        const transformedFields = {};
        data.data.verification_fields.components.forEach((field) => {
          if (field.type === "button") return;

          transformedFields[field.key] = {
            type: field.type === "datetime" ? "date" : "textfield" || "email",
            default: "",
            label: field.label,
            required: field.validate?.required || false,
          };
        });

        navigateToNextStep(transformedFields);
      } else {
        throw new Error(t("verify:failed_fetch_form_data"));
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast.error(t("verify:failed_load_form_fields"));
    }
  };

  const fetchOthersFormData = async () => {
    try {
      const response = await GET(
        `${EntityConstants.GET_CUSTOM_PROGRAMME_BATCH}?org_code=${postIssuerCode}`,
      );

      if (response.ok) {
        const data = await response.json();
        const transformedFields = {};
        data.data.verification_fields.components.forEach((field) => {
          if (field.type === "button") return;

          transformedFields[field.key] = {
            type: field.type === "datetime" ? "date" : "textfield" || "email",
            default: "",
            label: field.label,
            required: field.validate?.required || false,
          };
        });

        navigateToNextStep(transformedFields);
      } else {
        throw new Error(t("verify:failed_fetch_form_data_others"));
      }
    } catch (error) {
      console.error("Error fetching form data for Others:", error);
      toast.error(t("verify:failed_load_form_fields"));
    }
  };

  const navigateToNextStep = (formFields) => {
    const stateData = {
      postIssuerName,
      postIssuerCode,
      postProgrammeName,
      postProgrammeDisplayName,
      postProgrammeCode,
      postBatchName,
      postBatchCode,
      postCredentialTemplate,
      postCertificateName,
      isOthersSelected,
      isRegularProgramme,
      customProgrammeName,
      customBatchName,
      formFields,
      currentStep: 1,
    };

    navigate("/verifier/legacy/user-details", { state: stateData });
  };

  const getFormNextClick = async () => {
    const newErrors = {};
    if (!postIssuerName) newErrors.issuer = `${t("verify:msg_select_issuer")}`;
    if (isOthersSelected) {
      if (!customProgrammeName.trim()) {
        newErrors.programmeName = `${t("verify:msg_enter_programme_name")}`;
      }

      //  we have commented the batch code for now, can be used in future.

      // if (!customBatchName.trim()) {
      //   newErrors.batchName = `${t("verify:msg_enter_batch_name")}`;
      // }
    } else if (isRegularProgramme) {
      if (!postProgrammeName) {
        newErrors.programmeName = `${t("verify:msg_select_programme")}`;
      }
    } else {
      if (!postProgrammeName) {
        newErrors.programmeName = `${t("verify:msg_select_programme")}`;
      }

      //  we have commented the batch code for now, can be used in future.

      // if (!postBatchName) {
      //   newErrors.batchName = `${t("verify:msg_select_batch")}`;
      // }
    }
    if (!isOthersSelected && !isRegularProgramme && !postCredentialTemplate) {
      newErrors.credentialTemplate = `${t("verify:msg_select_template")}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      if (isOthersSelected) {
        await fetchOthersFormData();
      } else if (isRegularProgramme) {
        await fetchOthersFormData();
      } else {
        await fetchFormData();
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getIssuerDetails();
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (postIssuerName || postProgrammeName) {
        e.preventDefault();
        e.returnValue = `${t("verify:unsaved_changes_warning")}`;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [postIssuerName, postProgrammeName]);

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-[6rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={steps} currentStep={currentStep} />

      <div className="space-y-4">
        <div className="w-full flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:w-full flex flex-col gap-4 border border-black p-4 rounded-lg shadow-md">
            <div className="text-black text-lg font-semibold">
              {t("verify:certificate_details")}
            </div>
            <div className="w-full flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="w-full lg:w-full">
                  <label
                    className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                    htmlFor="legacy-issuer-select-input"
                  >
                    {t("verify:issuer")}
                  </label>
                  <Select
                    id="issuerName"
                    inputId="legacy-issuer-select-input"
                    instanceId="legacy-issuer-select"
                    name="issuerName"
                    value={selectedIssuer}
                    onChange={handleIssuerChange}
                    components={{
                      Option: WithoutQrIssuerSelectOption,
                      LoadingIndicator: SelectLoadingIndicator,
                    }}
                    options={issuerList.map((issuer) => ({
                      value: issuer.org_name,
                      label: issuer.org_name,
                      code: issuer.org_code,
                    }))}
                    placeholder={
                      isLoadingIssuers
                        ? t("verify:loading_issuers")
                        : t("verify:select_issuer")
                    }
                    className="basic-single"
                    classNamePrefix="select"
                    isDisabled={issuerList.length === 0}
                    isLoading={isLoadingIssuers}
                  />
                  <SelectLoadingStatus
                    id="legacy-issuer-loading-status"
                    message={isLoadingIssuers ? t("verify:loading_issuers") : ""}
                  />
                  {errors.issuer && (
                    <span
                      id="legacy-issuer-error-description"
                      className="text-red-600 text-sm"
                    >
                      {errors.issuer}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-full">
                  <label
                    className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                    htmlFor="legacy-programme-select-input"
                  >
                    {t("verify:programme_label")}
                  </label>
                  <Select
                    ref={programmeSelectRef}
                    id="programmeName"
                    inputId="legacy-programme-select-input"
                    name="programmeName"
                    value={selectedProgramme}
                    onChange={handleProgrammeChange}
                    onMenuOpen={() => setIsProgrammeMenuOpen(true)}
                    onMenuClose={() => setIsProgrammeMenuOpen(false)}
                    loadingMessageId="legacy-programme-loading-message"
                    slowLoadingHint={programmeSlowLoadingHint}
                    components={{
                      LoadingIndicator: SelectLoadingIndicator,
                      LoadingMessage: SelectLoadingMessage,
                    }}
                    options={
                      isLoadingProgrammes
                        ? []
                        : [
                            {
                              value: "Others",
                              label: `${t("verify:others")}`,
                              code: "Others",
                            },
                            ...programmeList.map((programme) => ({
                              value: programme.programme_name,
                              label: programme.programme_display_name
                                ? `${programme.programme_display_name} (${programme.programme_name})`
                                : programme.programme_name,
                              code: programme.programme_code,
                              displayLabel: programme.programme_display_name,
                            })),
                          ]
                    }
                    placeholder={
                      isLoadingProgrammes
                        ? t("verify:loading_programmes")
                        : t("verify:select_programme")
                    }
                    className="basic-single"
                    classNamePrefix="select"
                    isLoading={isLoadingProgrammes}
                    loadingMessage={() => t("verify:loading_programmes")}
                    isDisabled={isProgrammeSelectDisabled}
                  />
                  <SelectLoadingStatus
                    id="legacy-programme-loading-status"
                    message={
                      isLoadingProgrammes
                        ? programmeSlowLoadingHint ||
                          t("verify:loading_programmes")
                        : ""
                    }
                  />
                  {programmeSlowLoadingHint && !isProgrammeMenuOpen && (
                    <p
                      id="legacy-programme-slow-loading-hint"
                      className="mt-2 flex items-center gap-2 text-xs text-gray-500"
                    >
                      <SelectSpinner size="h-3 w-3" />
                      {programmeSlowLoadingHint}
                    </p>
                  )}
                  {programmeStatus === "error" && (
                    <div
                      id="legacy-programme-load-error"
                      role="alert"
                      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-red-600"
                    >
                      <span>{t("verify:error_loading_programmes")}</span>
                      <button
                        id="legacy-programme-retry-button"
                        type="button"
                        onClick={() => getProgrammeDetails(postIssuerCode)}
                        className="font-medium text-cfPrimary underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
                      >
                        {t("verify:retry")}
                      </button>
                    </div>
                  )}
                  {errors.programmeName && (
                    <span
                      id="legacy-programme-error-description"
                      className="text-red-600 text-sm"
                    >
                      {errors.programmeName}
                    </span>
                  )}

                  {showCustomProgrammeInput && (
                    <div className="mt-2">
                      <label
                        className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                        htmlFor="legacy-custom-programme-input"
                      >
                        {t("verify:programme_name")}
                      </label>
                      <input
                        type="text"
                        id="legacy-custom-programme-input"
                        value={customProgrammeName}
                        onChange={handleCustomProgrammeChange}
                        className="w-full bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                        placeholder={t("verify:enter_programme_name")}
                      />
                    </div>
                  )}
                </div>

                {/*  we have commented the batch code for now, can be used in future. */}

                {/* {(isOthersSelected || !isRegularProgramme) && (
                  <div className="w-full lg:w-full">
                    {isOthersSelected && (
                      <>
                        <label
                          className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                          htmlFor="batchName"
                        >
                          {t("verify:batch_label")}
                        </label>
                        <Select
                          id="batchName"
                          name="batchName"
                          value={selectedBatch}
                          onChange={handleBatchChange}
                          options={
                            isOthersSelected
                              ? [
                                  {
                                    value: "Others",
                                    label: `${t("verify:others")}`,
                                    code: "Others",
                                  },
                                ]
                              : batchList.map((batch) => ({
                                  value: batch.batch_name,
                                  label: batch.batch_name,
                                  code: batch.batch_code,
                                }))
                          }
                          placeholder={t("verify:select_batch")}
                          className="basic-single"
                          classNamePrefix="select"
                          isDisabled={
                            isOthersSelected ? false : batchList.length === 0
                          }
                        />
                      </>
                    )}
                    {errors.batchName && (
                      <span className="text-red-600 text-sm">
                        {errors.batchName}
                      </span>
                    )}

                    {showCustomBatchInput && (
                      <div className="mt-2">
                        <label
                          className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                          htmlFor="customBatch"
                        >
                          {t("verify:batch_name")}
                        </label>
                        <input
                          type="text"
                          id="customBatch"
                          value={customBatchName}
                          onChange={handleCustomBatchChange}
                          className="w-full bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                          placeholder={t("verify:enter_batch_name")}
                        />
                      </div>
                    )}
                  </div>
                )} */}

                {/* {!isOthersSelected && !isRegularProgramme && (
                  <div className="w-full lg:w-full">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                      htmlFor="credentialTemplate"
                    >
                      Templates
                    </label>
                    <Select
                      id="credentialTemplate"
                      name="credentialTemplate"
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                      options={templateList.map((template) => ({
                        value: template.issuer_credential_template_id,
                        label: template.certificate_name,
                      }))}
                      placeholder="Select Templates"
                      className="basic-single"
                      classNamePrefix="select"
                      isDisabled={templateList.length === 0}
                    />
                    {errors.credentialTemplate && (
                      <span className="text-red-600 text-sm">
                        {errors.credentialTemplate}
                      </span>
                    )}
                  </div>
                )} */}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                id="legacy-template-next-button"
                type="button"
                onClick={getFormNextClick}
                //  we have commented the batch code for now, can be used in future.
                // disabled={
                //   !postIssuerName ||
                //   // (isOthersSelected
                //   //   ? !customProgrammeName.trim()
                //   //   : !postProgrammeName) ||
                //   // (!isRegularProgramme && isOthersSelected
                //   //   ? !customBatchName.trim()
                //   // : !isRegularProgramme && !postBatchName) ||
                //   (!isOthersSelected &&
                //     !isRegularProgramme &&
                //     !postCredentialTemplate)
                // }
                disabled={
                  !postIssuerName ||
                  (isOthersSelected
                    ? !customProgrammeName.trim()
                    : !postProgrammeName) ||
                  (!isOthersSelected &&
                    !isRegularProgramme &&
                    !postCredentialTemplate)
                }
                className={`py-2.5 px-[3.40rem] text-sm font-medium text-white bg-cfButton hover:bg-cfPrimaryDark focus:outline-none rounded-[8px] border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100 ${
                  !postIssuerName ||
                  (isOthersSelected
                    ? !customProgrammeName.trim()
                    : !postProgrammeName) ||
                  //  we have commented the batch code for now, can be used in future.

                  // (!isRegularProgramme && isOthersSelected
                  //   ? !customBatchName.trim()
                  //   : !isRegularProgramme && !postBatchName) ||
                  (!isOthersSelected &&
                    !isRegularProgramme &&
                    !postCredentialTemplate)
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {t("verify:next")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegacyTemplateDetails;
